import {
	Action,
	AnyAction,
	configureStore,
	createListenerMiddleware,
	ThunkAction,
	ThunkDispatch,
} from '@reduxjs/toolkit'
import { CurriedGetDefaultMiddleware } from '@reduxjs/toolkit/dist/getDefaultMiddleware'
import { TypedUseSelectorHook, useDispatch, useSelector } from 'react-redux'

import alerts from './features/alerts/slice'
import app from './features/app/slice'
import appointments from './features/appointments/slice'
import auth from './features/auth/slice'
import azureCommunication from './features/azureCommunication/slice'
import carousel from './features/carousel/slice'
import chat from './features/chat/slice'
import chatbot from './features/chatbot/slice'
import clipboard from './features/clipboard/slice'
import cockpit from './features/cockpit/slice'
import contactLenses from './features/contactLenses/slice'
import coremedia from './features/coremedia/slice'
import countries from './features/countries/slice'
import diagnosiPlans from './features/diagnosiPlans/slice'
import diagnosis from './features/diagnosis/slice'
import dialog from './features/dialog/slice'
import errors from './features/errors/slice'
import exams from './features/exams/slice'
import { mfeReducer } from './features/mfe/mfeSlice'
import { contactsReducer } from './features/contacts/slice'
import healthDescriptions from './features/healthDescriptions/slice'
import medicalReportTemplates from './features/medicalReportTemplates/slice'
import notifications from './features/notifications/slice'
import ocularHealthConditions from './features/ocularHealthConditions/slice'
import panels from './features/panels/slice'
import patientMatch from './features/patientMatch/slice'
import patients from './features/patients/slice'
import practices from './features/practices/slice'
import prescriptions from './features/prescriptions/slice'
import referralCommons from './features/referralCommons/slice'
import referralDoctors from './features/referralDoctors/slice'
import renewal from './features/renewal/slice'
import reportFilters from './features/reportFilters/slice'
import reports from './features/reports/slice'
import roomSelectionPages from './features/roomSelection/slice'
import router from './features/router/slice'
import ses from './features/ses/slice'
import search from './features/search/slice'
import signDocuments from './features/signDocuments/slice'
import signRequests from './features/signRequests/slice'
import states from './features/states/slice'
import stores from './features/stores/slice'
import timezones from './features/timezones/slice'
import tasks from './features/tasks/slice'
import troubleShooting from './features/troubleShooting/slice'
import ui from './features/ui/slice'
import users from './features/users/slice'
import vaScales from './features/vaScales/slice'
import visualSimulation from './features/visualSimulation/slice'
import worklist from './features/worklist/slice'
import { RootState } from './model/model'
import { chiefComplaintCategoryApi } from './services/chiefComplaintCategory'
import { cptApi } from './services/cpt'
import { rtkQueryErrorLogger } from './services/errorCatchingMiddleware'
import { examsApi } from './services/exams'
import { fieldsDataApi } from './services/fieldsData'
import { fieldsDataMetaApi } from './services/fieldsDataMeta'
import { icd10Api } from './services/icd10'
import { icd10Api as icd10SystemApi } from './services/icd10.system'
import { loincApi } from './services/loinc'
import { cvxApi } from './services/cvx'
import { ndcApi } from './services/ndc'
import { manufacturerApi } from './services/manufacturer'
import { referralTypeApi } from './services/referralType'
import { snomedctApi } from './services/snomedct'
import { storesApi } from './services/stores'
import { usersApi } from './services/users'
import { worklistApi } from './services/worklist'
import { emergencyAccessApi } from './services/emergencyAccess'
import { appointmentTypeApi } from './services/appointments'
import { modifierApi } from './services/modifier'
import { sesApi } from './services/ses'
import { anesthesiaCategoriesApi } from './services/anesthesiaCategories'
import { surgeryConditionsApi } from './services/surgeryConditions'
import { visDocumentsApi } from './services/visDocuments'
import { cdaApi } from './services/cda'

export const listenerMiddleware = createListenerMiddleware<RootState>()

const reducer = {
	alerts,
	app,
	appointments,
	signDocuments,
	auth,
	azureCommunication,
	carousel,
	chat,
	chatbot,
	clipboard,
	cockpit,
	contactLenses,
	coremedia,
	countries,
	diagnosiPlans,
	diagnosis,
	dialog,
	errors,
	exams,
	healthDescriptions,
	medicalReportTemplates,
	notifications,
	panels,
	patientMatch,
	patients,
	practices,
	prescriptions,
	referralCommons,
	referralDoctors,
	renewal,
	reports,
	reportFilters,
	roomSelectionPages,
	router,
	search,
	signRequests,
	stores,
	states,
	tasks,
	timezones,
	troubleShooting,
	ui,
	users,
	contacts: contactsReducer,
	visualSimulation,
	ocularHealthConditions,
	vaScales,
	worklist,
	mfe: mfeReducer,
	ses,
	[icd10Api.reducerPath]: icd10Api.reducer,
	[snomedctApi.reducerPath]: snomedctApi.reducer,
	[loincApi.reducerPath]: loincApi.reducer,
	[cvxApi.reducerPath]: cvxApi.reducer,
	[ndcApi.reducerPath]: ndcApi.reducer,
	[manufacturerApi.reducerPath]: manufacturerApi.reducer,
	[cptApi.reducerPath]: cptApi.reducer,
	[referralTypeApi.reducerPath]: referralTypeApi.reducer,
	[chiefComplaintCategoryApi.reducerPath]: chiefComplaintCategoryApi.reducer,
	[fieldsDataApi.reducerPath]: fieldsDataApi.reducer,
	[fieldsDataMetaApi.reducerPath]: fieldsDataMetaApi.reducer,
	[icd10SystemApi.reducerPath]: icd10SystemApi.reducer,
	[worklistApi.reducerPath]: worklistApi.reducer,
	[examsApi.reducerPath]: examsApi.reducer,
	[usersApi.reducerPath]: usersApi.reducer,
	[storesApi.reducerPath]: storesApi.reducer,
	[sesApi.reducerPath]: sesApi.reducer,
	[emergencyAccessApi.reducerPath]: emergencyAccessApi.reducer,
	[appointmentTypeApi.reducerPath]: appointmentTypeApi.reducer,
	[modifierApi.reducerPath]: modifierApi.reducer,
	[anesthesiaCategoriesApi.reducerPath]: anesthesiaCategoriesApi.reducer,
	[surgeryConditionsApi.reducerPath]: surgeryConditionsApi.reducer,
	[visDocumentsApi.reducerPath]: visDocumentsApi.reducer,
	[cdaApi.reducerPath]: cdaApi.reducer,
}

const middleware = (getDefaultMiddleware: CurriedGetDefaultMiddleware) =>
	getDefaultMiddleware()
		.prepend(listenerMiddleware.middleware)
		.concat(
			icd10Api.middleware,
			snomedctApi.middleware,
			loincApi.middleware,
			cvxApi.middleware,
			ndcApi.middleware,
			manufacturerApi.middleware,
			cptApi.middleware,
			referralTypeApi.middleware,
			chiefComplaintCategoryApi.middleware,
			fieldsDataApi.middleware,
			fieldsDataMetaApi.middleware,
			icd10SystemApi.middleware,
			worklistApi.middleware,
			examsApi.middleware,
			usersApi.middleware,
			storesApi.middleware,
			sesApi.middleware,
			appointmentTypeApi.middleware,
			modifierApi.middleware,
			anesthesiaCategoriesApi.middleware,
			surgeryConditionsApi.middleware,
			emergencyAccessApi.middleware,
			visDocumentsApi.middleware,
			cdaApi.middleware,
			rtkQueryErrorLogger,
		)

const store = configureStore({
	reducer,
	middleware,
})

export function getTestStore(initialState: Partial<RootState>) {
	return configureStore({
		reducer,
		middleware,
		preloadedState: initialState,
	})
}

export type TeloGetState = () => RootState
export type TeloDispatch = ThunkDispatch<RootState, any, AnyAction>
export type AppThunk = ThunkAction<void, RootState, unknown, Action<string>>

export type AppThunkPromise<R = void> = ThunkAction<
	Promise<R>,
	RootState,
	unknown,
	Action<string>
>

export type WithDispatch = {
	dispatch: TeloDispatch
}

export const useTeloDispatch: () => TeloDispatch = useDispatch
export const useTeloSelector: TypedUseSelectorHook<RootState> = useSelector

export default store
